import React from "react";
import PropTypes from "prop-types";
import moment from "moment/moment";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import Icon from "@material-ui/core/Icon";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardIcon from "components/Card/CardIcon";
import CardHeader from "components/Card/CardHeader";

import styles from "../../assets/jss/material-dashboard-pro-react/views/initiativesStyle";

const useStyles = makeStyles(styles);

/**
 * Component that displays initiative statistics cards
 * @param {Object} props - Component props
 * @param {Function} props.getTotalBenefit - Function to get total benefit value
 * @param {Function} props.getInitiativesCount - Function to get initiatives count
 * @param {Function} props.getInitiativesInAttentionCount - Function to get initiatives in attention count
 * @param {Function} props.getOutdatedInitiativesCount - Function to get outdated initiatives count
 * @param {Function} props.t - Translation function
 * @param {Object} props.i18n - i18n object for language detection
 */
const InitiativesStatsCards = ({ initiatives, t, i18n }) => {
  const classes = useStyles();

  const isActive = (init) => init.status === "active";
  const isActiveNonOpportunity = (init) =>
    init.status === "active" && init.stage !== "opportunity";

  const getTotalBenefit = () =>
    Object.values(initiatives)
      .filter(isActiveNonOpportunity)
      .reduce((total, init) => total + init.margin_benefit_global, 0.0);
  const getInitiativesCount = () =>
    Object.values(initiatives).filter(isActiveNonOpportunity).length;
  const getInitiativesInAttentionCount = () =>
    Object.values(initiatives).filter(
      (init) => init.follow_up?.[0]?.traffic_light > 1,
    ).length;
  const getOutdatedInitiativesCount = () =>
    Object.values(initiatives).filter((init) => {
      if (!isActive(init)) return false;
      const last_event = moment
        .utc(init.follow_up?.[0]?.date, "YYYY-MM-DD HH:mm")
        .local();
      const today = moment().startOf("day");
      return moment.duration(today.diff(last_event)).asDays() > 28;
    }).length;

  return (
    <GridContainer style={{ marginTop: "-5px", marginBottom: "-15px" }}>
      <GridItem xs={12} sm={6}>
        <Card className={classes.card}>
          <CardHeader color="client" stats icon>
            <CardIcon color="client" className={classes.cardIcon}>
              <Icon>receipt</Icon>
            </CardIcon>
            <p className={classes.cardCategory}>
              {t("initiatives.value_to_capture")}
            </p>
            <h3 className={classes.cardTitle}>
              {(100 * getTotalBenefit()).toFixed(1)}
              <span className={classes.verySmall}>
                {" "}
                p.p. {t("initiatives.in")}{" "}
              </span>
              {getInitiativesCount().toString()}
              <span className={classes.verySmall}>
                {" "}
                {getInitiativesCount() <= 1 &&
                (i18n.language === "es" || i18n.language === "pt")
                  ? t("initiatives.initiatives_lower")
                  : t("initiatives.initiatives_lower_plural")}
              </span>
            </h3>
          </CardHeader>
        </Card>
      </GridItem>

      <GridItem xs={12} sm={6}>
        <Card
          className={cx({
            [classes.card]: true,
            ["responsive"]: true,
          })}
        >
          <CardHeader color="client" stats icon>
            <p className={classes.cardCategory}>
              {t("initiatives.initiatives_to_check")}
            </p>
            <h3 className={classes.cardTitle}>
              {getInitiativesInAttentionCount().toString()}
              <span className={classes.verySmall}>
                {" "}
                {t("initiatives.in_attention")} {t("initiatives.and")}{" "}
              </span>
              {getOutdatedInitiativesCount().toString()}
              <span className={classes.verySmall}>
                {" "}
                {getOutdatedInitiativesCount() === 0 &&
                i18n.language !== "es" &&
                i18n.language !== "pt"
                  ? t("initiatives.outdated_plural")
                  : getOutdatedInitiativesCount() <= 1
                    ? t("initiatives.update")
                    : t("initiatives.outdated_plural")}
              </span>
            </h3>
          </CardHeader>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

InitiativesStatsCards.propTypes = {
  initiatives: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
};

export default InitiativesStatsCards;
